import { DECIMAL_PLACES_DEFAULT } from "@/constants";
import {
  GeneratePurchaseOrderPayload,
  GeneratePurchaseOrderPayloadAdditionalProductProps,
  GeneratePurchaseOrderPayloadSupplyItemProps,
  GeneratePurchaseOrderPreviewPayload,
  SaleOrderProps,
  SupplierPaymentPriorityEnum,
  SupplierProps,
  SupplyOrderItemProps,
  SupplyOrderShipmentStatusEnum,
} from "@/types";

import * as Yup from "yup";

export type GeneratePurchaseOrderValues = {
  currency?: "MXN" | "USD";
  supplier?: SupplierProps;
  saleOrders?: SaleOrderProps[];
  supplyOrderItems?: SupplyOrderItemProps[];
  additionalProducts?: GeneratePurchaseOrderPayloadAdditionalProductProps[];
  purchaseOrderCode?: string;
  expectedPaymentDate?: string;
  paymentPriority?: keyof typeof SupplierPaymentPriorityEnum;
  shipmentStatus?: keyof typeof SupplyOrderShipmentStatusEnum;
  observation?: string;
  invoicePDFFile?: File;
  invoiceXMLFile?: File;
  supplierWarehouseUuid?: string;
};

export const schemaPO = (t: (key: string) => string) => ({
  supplierUuid: Yup.string().required(t("requiredMessage")),
  purchaseOrderCode: Yup.string().required(t("requiredMessage")),
});

const getSaleOrdersFromPayload = (
  values: GeneratePurchaseOrderValues,
  saleOrdersToPayload?: Record<
    string,
    Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
  >
): GeneratePurchaseOrderPayload["saleOrders"] => {
  if (!saleOrdersToPayload) return [];

  return Object.entries(saleOrdersToPayload).map(
    ([saleOrderUuid, itemsObj]) => {
      const saleOrder = values?.saleOrders?.find(
        (so) => so?.uuid === saleOrderUuid
      );
      const reference = saleOrder?.reference || "";

      const itemsArray: {
        uuid: string;
        supplyItems: GeneratePurchaseOrderPayloadSupplyItemProps[];
      }[] = [];

      Object.entries(itemsObj).forEach(([itemUuid, items]) => {
        if (!items || items.length === 0) return;
        itemsArray.push({
          uuid: itemUuid,
          supplyItems: items?.filter((item) => item?.quantity) || [],
        });
      });

      return {
        reference,
        items: itemsArray,
      };
    }
  );
};

export const formattedPayloadToGeneratePurchaseOrder = (
  values: GeneratePurchaseOrderValues,
  saleOrdersToPayload?: Record<
    string,
    Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
  >
): GeneratePurchaseOrderPayload => {
  const saleOrders: GeneratePurchaseOrderPayload["saleOrders"] =
    getSaleOrdersFromPayload(values, saleOrdersToPayload);

  return {
    currency: values?.currency || "USD",
    status: "accepted",
    purchaseOrderCode: values?.purchaseOrderCode,
    paymentPriority: values?.paymentPriority,
    expectedPaymentDate: values?.expectedPaymentDate,
    observation: values?.observation,
    supplierUuid: values?.supplier?.uuid,
    supplierWarehouseUuid: values?.supplierWarehouseUuid,
    saleOrders,
    additionalProducts: values?.additionalProducts?.map((item) => ({
      ...item,
      catalogProduct: undefined,
      cost: String(Number(item?.cost).toFixed(DECIMAL_PLACES_DEFAULT)),
    })),
  };
};

const getSaleOrdersPreviewFromPayload = (
  values: GeneratePurchaseOrderValues,
  saleOrdersToPayload?: Record<
    string,
    Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
  >
): GeneratePurchaseOrderPreviewPayload["saleOrders"] => {
  if (!saleOrdersToPayload) return [];

  return Object.entries(saleOrdersToPayload).map(
    ([saleOrderUuid, itemsObj]) => ({
      reference: values?.saleOrders?.find((so) => so?.uuid === saleOrderUuid)
        ?.reference,
      items: Object.entries(itemsObj).flatMap(([itemUuid, items]) =>
        items
          .filter((item) => item.generatePo)
          .map((item) => ({
            uuid: itemUuid || item?.uuid || "",
            quantity: item.quantity !== undefined ? item.quantity : 0,
          }))
      ),
    })
  );
};

export const formattedPayloadToGeneratePurchaseOrderPreview = (
  values: GeneratePurchaseOrderValues,
  saleOrdersToPayload?: Record<
    string,
    Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
  >
): GeneratePurchaseOrderPreviewPayload => {
  const saleOrders: GeneratePurchaseOrderPreviewPayload["saleOrders"] =
    getSaleOrdersPreviewFromPayload(values, saleOrdersToPayload);

  return {
    supplierUuid: values?.supplier?.uuid,
    saleOrders,
    additionalProducts: values?.additionalProducts?.map((item) => ({
      ...item,
      catalogProduct: undefined,
      cost: String(Number(item?.cost).toFixed(DECIMAL_PLACES_DEFAULT)),
    })),
    currency: values?.currency,
  };
};
